import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import { Badge, cn } from "@devhop/ui";
import {
  BanknoteIcon,
  ClockIcon,
  GraduationCapIcon,
  UsersIcon,
} from "lucide-react";
import type { InstructorPayrollBatchSummaryDTO } from "../api/client";
import {
  formatHours,
  formatPayoutKip,
  type PayoutBatchWorkflowStep,
  type PayoutTableFilter,
} from "../lib/instructor-payout-display";
import { InstructorMonthlyPayoutBatchStepper } from "./InstructorMonthlyPayoutBatchStepper";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "./ReportSummaryStatCard";

export type InstructorMonthlyPayoutSummaryTotals = {
  instructors: number;
  sessions: number;
  hours: number;
  payout: number;
  generalClassPayout: number;
  privateLessonPayout: number;
  partnerPayout: number;
  roomRentalAssistPayout: number;
  generalClassAssistPayout: number;
};

type InstructorMonthlyPayoutSummaryCardsProps = {
  isLoading: boolean;
  run: { year: number; month: number } | null;
  totals: InstructorMonthlyPayoutSummaryTotals;
  savedBatch?: InstructorPayrollBatchSummaryDTO | null;
  workflowStep?: PayoutBatchWorkflowStep;
  batchLiveDelta?: {
    savedTotal: number;
    liveTotal: number;
    delta: number;
  } | null;
  activeTableFilter?: PayoutTableFilter;
  onTableFilterChange?: (filter: PayoutTableFilter) => void;
};

function batchStatusBadge(
  batch: InstructorPayrollBatchSummaryDTO | null | undefined,
) {
  if (!batch) return null;
  if (batch.status === "paid") {
    return (
      <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700">
        ຈ່າຍແລ້ວ
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="bg-amber-500/15 text-amber-800">
      ຢືນຢັນແລ້ວ · ລໍຖ້າຈ່າຍ
    </Badge>
  );
}

export function InstructorMonthlyPayoutSummaryCards({
  isLoading,
  run,
  totals,
  savedBatch,
  workflowStep = "review",
  batchLiveDelta,
  activeTableFilter = "all",
  onTableFilterChange,
}: InstructorMonthlyPayoutSummaryCardsProps) {
  if (!run) {
    return (
      <div className="mb-6 rounded-xl border border-dashed bg-muted/20 px-4 py-8 text-center text-muted-foreground text-sm">
        ເລືອກເດືອນເພື່ອເບິ່ງສະຫຼຸບຄ່າຈ້າງ
      </div>
    );
  }

  function FilterChip({
    filter,
    label,
    amount,
  }: {
    filter: PayoutTableFilter;
    label: string;
    amount: number;
  }) {
    if (amount <= 0 || !onTableFilterChange) {
      return <span>{label} {formatPayoutKip(amount)}</span>;
    }
    const active = activeTableFilter === filter;
    return (
      <button
        type="button"
        className={cn(
          "rounded px-1 py-0.5 transition-colors hover:bg-muted",
          active && "bg-primary/10 font-medium text-primary ring-1 ring-primary/30",
        )}
        onClick={() =>
          onTableFilterChange(active ? "all" : filter)
        }
      >
        {label} {formatPayoutKip(amount)}
      </button>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-sm tabular-nums">
          ຮອບ {run.month}/{run.year}
        </span>
        {batchStatusBadge(savedBatch)}
        {savedBatch ? (
          <span className="text-muted-foreground text-xs tabular-nums">
            ບັນທຶກ {formatPayoutKip(savedBatch.totalAmountKip)} · ຄູ{" "}
            {savedBatch.instructorCount} · {savedBatch.dateFrom} →{" "}
            {savedBatch.dateTo}
          </span>
        ) : null}
      </div>

      <InstructorMonthlyPayoutBatchStepper currentStep={workflowStep} />

      {batchLiveDelta ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-sm leading-relaxed dark:text-amber-200">
          ຄຳນວນ live ({formatPayoutKip(batchLiveDelta.liveTotal)}) ຕ່າງຈາກຍອດບັນທຶກ (
          {formatPayoutKip(batchLiveDelta.savedTotal)}) ປະມານ{" "}
          {Math.abs(batchLiveDelta.delta).toLocaleString("lo-LA")} ₭ — ອາດມີການເຊັກຊື່
          ໃໝ່ຫຼັງຢືນຢັນຮອບ
        </div>
      ) : null}

      <ReportSummaryStatGrid columns={4} className="mb-0">
        <ReportSummaryStatCard
          title="ຄູສອນ"
          icon={UsersIcon}
          tone="students"
          isLoading={isLoading}
          value={reportSummaryCount(totals.instructors)}
        />
        <ReportSummaryStatCard
          title="ຊົ່ວໂມງຮຽນລວມ"
          icon={GraduationCapIcon}
          tone="packages"
          isLoading={isLoading}
          value={reportSummaryCount(totals.sessions)}
        />
        <ReportSummaryStatCard
          title="ຊົ່ວໂມງລວມ"
          icon={ClockIcon}
          tone="attendance"
          isLoading={isLoading}
          value={formatHours(totals.hours)}
        />
        <ReportSummaryStatCard
          title="ຄ່າຈ້າງລວມ"
          icon={BanknoteIcon}
          tone="refunds"
          isLoading={isLoading}
          className="border-red-600/20 bg-red-600/5"
          value={
            <>
              <CompactMoneyAmount
                amount={totals.payout}
                className="text-red-700 dark:text-red-400"
              />
              <div className="mt-2 flex flex-wrap gap-1.5 text-muted-foreground text-xs">
                <FilterChip
                  filter="general_class"
                  label="ຄູຫຼັກ"
                  amount={totals.generalClassPayout}
                />
                <span aria-hidden>·</span>
                <FilterChip
                  filter="general_class_assist"
                  label="TA"
                  amount={totals.generalClassAssistPayout ?? 0}
                />
                <span aria-hidden>·</span>
                <FilterChip
                  filter="private_lesson"
                  label="ສ່ວນຕົວ"
                  amount={totals.privateLessonPayout}
                />
                {totals.partnerPayout > 0 ? (
                  <>
                    <span aria-hidden>·</span>
                    <FilterChip
                      filter="partner"
                      label="Partner"
                      amount={totals.partnerPayout}
                    />
                  </>
                ) : null}
                {(totals.roomRentalAssistPayout ?? 0) > 0 ? (
                  <>
                    <span aria-hidden>·</span>
                    <FilterChip
                      filter="room_rental_assist"
                      label="ຊ່ວຍສອນເຊົ່າຫ້ອງ"
                      amount={totals.roomRentalAssistPayout}
                    />
                  </>
                ) : null}
              </div>
              {onTableFilterChange ? (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  ຄລິກປະເພດເພື່ອກັ່ນຕາຕະລາງ
                </p>
              ) : null}
            </>
          }
        />
      </ReportSummaryStatGrid>
    </div>
  );
}
